import { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import type { DataTableFilterKind, IDataTableExportRequest, IDataTableFacetValue } from './DataTable.types';
import { ValueType } from '../types/ValueType';
import { dateFilterSpan, filterKindOf } from './dataTableFilters';

/**
 * Серверный источник данных таблицы.
 *
 * Формат запроса намеренно оставлен DX-образным (`skip`/`take`/`sort`/`filter`):
 * это проводной контракт бэкенда, а не деталь старого грида — его понимают все
 * существующие эндпоинты списков. Атом же оперирует своим состоянием (сортировка,
 * фильтры, страница) и переводит его в этот формат здесь, в одном месте.
 */

/** Сортировка в формате запроса. */
export interface IServerSort {
    selector: string;
    desc: boolean;
}

/** Уровень группировки в запросе (DX-протокол): сервер вернёт группы уровня
 *  без элементов; skip/take при этом действуют по группам, а не по строкам. */
export interface IServerGroupDescriptor {
    selector: string;
    desc: boolean;
    /** Всегда false: элементы группы догружаются отдельным плоским запросом. */
    isExpanded: false;
}

/**
 * Что запрашивает таблица:
 * - `rows` — плоская выборка (страница или порция строк без группировки); такие же
 *   параметры получают выгрузка и «выбрать все» при серверной группировке;
 * - `groups` — страница групп уровня `path.length` (селектор — `group[0]`), `path` — ключи
 *   родительских групп, у верхнего уровня `[]`;
 * - `groupRows` — строки внутри группы с ключами `path` (по уровням группировки).
 *
 * Условия пути (`[поле, '=', ключ]`) таблица уже дописала в filter: признак избавляет
 * загрузчик от угадывания по filter и group, что именно у него просят.
 */
export type ServerLoadScope =
    { kind: 'rows' } | { kind: 'groups'; path: unknown[] } | { kind: 'groupRows'; path: unknown[] };

/** Параметры выборки, которые уходят на сервер. */
export interface IServerLoadOptions {
    skip: number;
    take: number;
    sort?: IServerSort[];
    /** Фильтр в DX-нотации: ['field', 'op', value] и группы с 'and' / 'or'. */
    filter?: unknown;
    requireTotalCount: true;
    /** Запрос уровня групп вместо строк (см. useDataTableServerGroups). */
    group?: IServerGroupDescriptor[];
    requireGroupCount?: boolean;
    /** Что запрашивается (см. ServerLoadScope). Таблица заполняет его в каждом запросе для
     *  загрузчика; серверу он не адресован: CP берёт в запрос только известные ключи, а Front
     *  (`DataGrid`) шлёт параметры целиком — WebAPI незнакомое поле пропускает. */
    scope?: ServerLoadScope;
}

/** Запрос всей выборки, а не содержимого раскрытой группы: плоский список или группы
 *  верхнего уровня — только в нём filter без условий пути к группе. Параметры без
 *  признака (собранные вне таблицы) считаются плоской выборкой. */
export const isWholeSelectionRequest = (options: IServerLoadOptions): boolean =>
    !options.scope || options.scope.kind === 'rows' || options.scope.path.length === 0;

/** Группа из серверного ответа: ключ, отформатированная подпись (адресная
 *  книга, «Да/Нет», потоки — форматирует бэкенд) и количество строк. */
export interface IServerGroup {
    key: unknown;
    caption?: string | null;
    count: number;
}

export interface IServerLoadResult<T> {
    rows: T[];
    totalCount: number;
    /** Заполняются только в ответ на запрос с group. */
    groups?: IServerGroup[];
    groupCount?: number;
}

/** Загрузчик: получает параметры выборки, отдаёт строки и общее количество. */
export type ServerLoader<T> = (options: IServerLoadOptions, signal: AbortSignal) => Promise<IServerLoadResult<T>>;

/**
 * Источник серверной таблицы (проп `source` у DataTable): загрузчик выборки и необязательные возможности поверх
 * неё. Запросы возможностей таблица собирает сама из своего состояния, отменяет устаревшие сигналом и показывает
 * ход и отказы — странице остаётся передать один объект. Источник делают фабрики приложений (OData, dxlo) и держат
 * стабильным (useMemo по параметрам набора).
 */
export interface IDataTableSource<T> {
    /** Загрузчик выборки. Смена функции — другая выборка: окно строк, прокрутка, выделение и ключи «выбрать всё»
     *  сбрасываются (как serverResetToken). Фабрики держат его стабильным (useMemo по параметрам набора). */
    load: ServerLoader<T>;
    /** Смена значения при том же `load` перечитывает то же место (после сохранения, «Обновить»). */
    refreshToken?: number;
    /** Ключи всех строк выборки с параметрами последней загрузки — «выбрать всё» по всем страницам; с ним
     *  `selectAllMode` по умолчанию `allPages`. Без него `allPages` берёт ключи у `load` (весь набор,
     *  `skip=0`/`take=0`). Сигнал отменяет запрос при смене выборки, отмене выбора и размонтировании. */
    keys?: (options: IServerLoadOptions, signal: AbortSignal) => Promise<string[]>;
    /** Значения воронки колонки. Сигнал отменяет запрос, когда воронку закрыли, выборка сменилась или таблица
     *  размонтирована; отказ — сообщение в воронке с «Повторить». */
    facets?: (request: IDataTableFacetRequest, signal: AbortSignal) => Promise<IDataTableFacetValue[]>;
    /** Значения воронки порциями: с ним воронка грузит список при прокрутке, а поиск уходит на сервер условием
     *  строки фильтров колонки. `facets` при этом остаётся для подписей отмеченных значений в чипах. */
    facetPage?: (request: IDataTableFacetPageRequest, signal: AbortSignal) => Promise<IDataTableFacetPage>;
    /** Выгрузка, которую собирает сервер (как serverExport): ход на кнопке, отказ — уведомлением, объём —
     *  `exportLimitModal`. Сигнал отменяет её при размонтировании таблицы. */
    export?: (request: IDataTableExportRequest, signal: AbortSignal) => Promise<void>;
    /** Перед выгрузкой — окно выбора объёма (сколько строк выгрузить). Выбирать объём может не каждый — решает
     *  фабрика источника (CP — администратор); явный `serverExportLimitModal` таблицы сильнее. */
    exportLimitModal?: boolean;
    /** Выгрузка фоновой задачей (вместе с `export`): без него — только `export`. */
    backgroundExport?: IDataTableBackgroundExport;
    /** Поля глобального поиска на сервере. */
    searchFields?: string[];
}

/** Запрос фоновой выгрузки: тот же, что у `export`, и почему выгрузка ушла в фон. */
export interface IDataTableBackgroundExportRequest extends IDataTableExportRequest {
    /** Фон выбрала таблица по порогу `suggestAboveRows`, а не пользователь: сообщению о старте — сказать почему. */
    suggested: boolean;
    /** Кнопка выгрузки: от неё приложение может показать, куда ушла задача. */
    origin?: Element;
}

/**
 * Фоновая выгрузка источника: файл готовит задача на сервере, и уход со страницы её не отменяет — поэтому сигнала у
 * `start` нет. Где показывать ход и итог (колокольчик и т. п.), решает приложение.
 */
export interface IDataTableBackgroundExport {
    /**
     * Выше скольких строк выборки выгрузка уходит в фон без вопроса, а в окне объёма фон отмечен заранее. Нет — фон
     * только по выбору в окне объёма.
     */
    suggestAboveRows?: number;
    /**
     * Поставить выгрузку задачей. `unavailable` — фона нет (выключен на сервере): таблица выгружает обычным `export`.
     * Отказ — уведомление таблицы, как у `export`.
     */
    start: (request: IDataTableBackgroundExportRequest) => Promise<'started' | 'unavailable'>;
}

/** Фон без вопроса: число строк выборки известно и выше порога `suggestAboveRows`. */
export const suggestsBackgroundExport = (
    background: Pick<IDataTableBackgroundExport, 'suggestAboveRows'> | undefined,
    rows: number | undefined,
): boolean => {
    const threshold = background?.suggestAboveRows;
    return threshold != null && rows != null && rows > threshold;
};

/** Запрос значений воронки колонки у источника. */
export interface IDataTableFacetRequest {
    field: string;
    /**
     * Параметры текущей выборки (фильтр, поиск, сортировка; `skip=0`, `take=0` — весь набор) без отметок в воронке
     * этой колонки — семантика клиентской воронки: остальные колонки, поиск и строка фильтров самой колонки сужают
     * список, а свой выбор — нет, иначе после отметки значения в списке осталось бы только оно.
     */
    options: IServerLoadOptions;
    /** Отмеченные сейчас значения колонки (ключи `IDataTableFacetValue`, null — «(Пусто)») — остаются в списке,
     *  даже если в выборке их нет (счётчик 0): иначе их не снять по одному. */
    selected: (string | null)[];
}

/**
 * Запрос порции значений воронки. В `options.filter` таблица уже сложила выборку колонки (как у `facets`), поиск по
 * значениям и, для отмеченных, условие «значение из списка» — источнику остаётся сгруппировать по полю и отдать
 * группы с `skip` по `skip + take` в порядке значений.
 */
export interface IDataTableFacetPageRequest {
    field: string;
    options: IServerLoadOptions;
    skip: number;
    take: number;
}

/** Порция значений воронки: `hasMore` — за ней есть ещё. */
export interface IDataTableFacetPage {
    values: IDataTableFacetValue[];
    hasMore: boolean;
}

/** Перевод стабильного ключа строки в нулевой номер серверной порции. */
export type ServerRowPageLocator = (
    rowId: string,
    options: IServerLoadOptions,
    signal: AbortSignal,
) => Promise<number | undefined>;

/** Значение фильтра колонки в состоянии таблицы (см. dataTableFilters). */
interface IColumnFilterValue {
    row?: { op: string; value: unknown };
    header?: (string | null)[];
}

/** Операции filter-row в нотацию запроса: словарь один на клиент и сервер. */
const OP_MAP: Record<string, string> = {
    contains: 'contains',
    notcontains: 'notcontains',
    startswith: 'startswith',
    endswith: 'endswith',
    '=': '=',
    '<>': '<>',
    '<': '<',
    '>': '>',
    '<=': '<=',
    '>=': '>=',
};

/**
 * Значение условия для сервера.
 *
 * Фильтры на клиенте хранятся строками (значение инпута), а бэкенд разбирает их
 * по типу поля и на строке «true» падает («The input string 'true' was not in a
 * correct format») — для boolean-колонок отправляем настоящие булевы. Только для
 * них: текстовый фильтр по слову «true» должен уйти строкой.
 */
function filterValue(value: unknown, kind?: DataTableFilterKind): unknown {
    if (kind !== 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
}

/** Условие filter-row по дате — интервал точности значения (`dateFilterSpan`): дата без времени — день целиком,
 *  как у DevExtreme (на это рассчитаны dxlo-бэкенды), `12:30` — минута, `12:30:15` — секунда. `= t` — сам
 *  интервал, `<> t` — всё, кроме него, `<= t` — по его конец (`< t+1`), `> t` — со следующего (`>= t+1`), `<` и
 *  `>=` — от его начала. Значение с долями секунды уходит как есть. Тот же смысл у клиентского сравнения
 *  (`dxMatch`). */
function dateExpression(field: string, op: string, value: unknown): unknown {
    const span = dateFilterSpan(value);
    if (!span) return [field, OP_MAP[op] ?? '=', value];
    switch (op) {
        case '=':
            return [[field, '>=', span.startIso], 'and', [field, '<', span.endIso]];
        case '<>':
            return [[field, '<', span.startIso], 'or', [field, '>=', span.endIso]];
        case '<=':
            return [field, '<', span.endIso];
        case '>':
            return [field, '>=', span.endIso];
        default:
            return [field, OP_MAP[op] ?? '=', span.startIso];
    }
}

/** Один фильтр колонки → выражение запроса (или null, если фильтровать нечего). */
function filterExpression(field: string, value: IColumnFilterValue, kind?: DataTableFilterKind): unknown {
    const parts: unknown[] = [];
    const row = value.row;
    if (row && row.value !== undefined && row.value !== '' && row.value !== null) {
        if (row.op === 'between' && Array.isArray(row.value)) {
            const [from, to] = row.value as [unknown, unknown];
            // у дат «между a и b» — >= a и < b + единица точности b (у дня — весь день b)
            if (from) parts.push(kind === 'date' ? dateExpression(field, '>=', from) : [field, '>=', from]);
            if (to) parts.push(kind === 'date' ? dateExpression(field, '<=', to) : [field, '<=', to]);
        } else if (kind === 'boolean') {
            // Тип сохраняем в проводном контракте: сервер отличает boolean «Нет»
            // (куда входит незаданный флаг) от числового условия «не равно 1».
            parts.push([field, '=', filterValue(row.value, kind)]);
        } else if (kind === 'date') {
            parts.push(dateExpression(field, row.op, row.value));
        } else {
            parts.push([field, OP_MAP[row.op] ?? '=', row.value]);
        }
    }
    // header-фильтр: набор выбранных значений — это «или» по равенству;
    // null — «(Пустые)», уходит условием равенства NULL
    const header = value.header ?? [];
    if (header.length) {
        const alternatives: unknown[] = [];
        header.forEach((v, i) => {
            if (i) alternatives.push('or');
            alternatives.push([field, '=', v === null ? null : filterValue(v, kind)]);
        });
        parts.push(alternatives.length === 1 ? alternatives[0] : alternatives);
    }
    if (!parts.length) return null;
    if (parts.length === 1) return parts[0];
    const joined: unknown[] = [];
    parts.forEach((p, i) => {
        if (i) joined.push('and');
        joined.push(p);
    });
    return joined;
}

/** Состояние таблицы → параметры плоской серверной выборки (scope `rows`). */
export function buildServerLoadOptions({
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    globalFilter,
    searchFields,
    filterKinds,
}: {
    pageIndex: number;
    pageSize: number;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    /** Строка глобального поиска: разворачивается в «или» по указанным полям. */
    globalFilter?: string;
    searchFields?: string[];
    /** Вид фильтра по полю: без него условие уходит как есть, а даты и boolean
     *  требуют серверной семантики (диапазон дня, «Нет» с NULL). */
    filterKinds?: Record<string, DataTableFilterKind>;
}): IServerLoadOptions {
    const parts: unknown[] = [];
    for (const f of columnFilters) {
        const expr = filterExpression(f.id, f.value as IColumnFilterValue, filterKinds?.[f.id]);
        if (expr) parts.push(expr);
    }
    if (globalFilter && searchFields?.length) {
        const alternatives: unknown[] = [];
        searchFields.forEach((field, i) => {
            if (i) alternatives.push('or');
            alternatives.push([field, 'contains', globalFilter]);
        });
        parts.push(alternatives.length === 1 ? alternatives[0] : alternatives);
    }
    const filter: unknown[] = [];
    parts.forEach((p, i) => {
        if (i) filter.push('and');
        filter.push(p);
    });

    // Поле take на бэке — Int32: режим «Все» (и старые сохранённые состояния с
    // MAX_SAFE_INTEGER) не должны ронять десериализацию запроса.
    const INT32_MAX = 2147483647;
    const take = Math.min(pageSize, INT32_MAX);
    return {
        skip: Math.min(pageIndex * take, INT32_MAX),
        take,
        ...(sorting.length ? { sort: sorting.map((s) => ({ selector: s.id, desc: s.desc })) } : {}),
        ...(filter.length ? { filter: filter.length === 1 ? filter[0] : filter } : {}),
        requireTotalCount: true,
        scope: { kind: 'rows' },
    };
}

/** Текст в выборке как есть: время, год, размер файла и «Да/Нет» хранятся не строкой подписи. */
const SERVER_SEARCH_TEXT = new Set<ValueType | undefined>([
    undefined,
    ValueType.Unknown,
    ValueType.Text,
    ValueType.LongText,
    ValueType.Url,
    ValueType.MultipleString,
]);

/** Поиск по значениям воронки уходит на сервер у текста («содержит») и чисел («равно»). У дат и флагов набранный
 *  текст в условие не разобрать, у адресной книги и потока в выборке ключ, а в списке подпись, — они (и прочие
 *  нетекстовые типы) ищутся по загруженному списку. */
export const serverFacetSearch = (dataType?: ValueType): boolean =>
    filterKindOf(dataType) === 'number' || SERVER_SEARCH_TEXT.has(dataType);

/**
 * Фильтр порции воронки: выборка колонки (`options.filter`), поиск по значениям (у колонок с `serverFacetSearch`) и
 * отбор по ключам (`keys` — отмеченные значения). `null` — совпадений быть не может: в поиске по числу не число.
 */
export function facetPageFilter(
    options: IServerLoadOptions,
    field: string,
    { search, keys }: { search?: string; keys?: readonly (string | null)[] },
    kind?: DataTableFilterKind,
): unknown {
    const query = search?.trim();
    let row: IColumnFilterValue['row'];
    if (query && kind === 'number') {
        const number = Number(query.replace(/\s/g, '').replace(',', '.'));
        if (!Number.isFinite(number)) return null;
        row = { op: '=', value: String(number) };
    } else if (query) {
        row = { op: 'contains', value: query };
    }
    const own = filterExpression(field, { row, header: keys?.length ? [...keys] : undefined }, kind);
    if (!own) return options.filter;
    return options.filter !== undefined ? [options.filter, 'and', own] : own;
}

/**
 * Сервер отдал не все значения воронки (`facets`, `getHeaderFilterValues`): `limit` — сколько. `by: 'values'` —
 * список из первых `limit` значений; `'rows'` — значения собраны по первым `limit` строкам выборки (состав и
 * счётчики — только по ним). Воронка показывает пометку под «Выделить все».
 */
export interface IDataTableFacetLimit {
    limit: number;
    by: 'values' | 'rows';
}

/** Пометить ответ воронки урезанным (`limit`; без него — как есть). Пометка — неперечисляемое свойство массива: тип
 *  ответа и сравнение значений прежние, но новый массив (`map`, `filter`) её теряет — ставить последней. */
export const withFacetLimit = <T extends IDataTableFacetValue[]>(values: T, limit?: IDataTableFacetLimit): T =>
    limit ? Object.defineProperty(values, 'facetLimit', { value: limit, configurable: true }) : values;

/** Пометка урезанного ответа воронки, поставленная `withFacetLimit` */
export const facetLimitOf = (values: readonly IDataTableFacetValue[]): IDataTableFacetLimit | undefined =>
    (values as { facetLimit?: IDataTableFacetLimit }).facetLimit;
